/**
 * useAppCursorCallBridge — talks to the AppCursor extension when this page
 * runs as its hidden "call host" iframe (`?target=appcursor&callHost=1`).
 *
 * The extension keeps one hidden iframe per Twilio app in every CRM tab, so
 * the Twilio Device stays registered even when no Twilio page is open. This
 * hook forwards the Device's state up to the extension, which draws the
 * incoming-call toast in the CRM page (same corner as message toasts):
 *
 *   iframe → extension  (ulgId 'UA_FLOAT_APP_CONTENT', like the config iframe)
 *     renderUAFAppIncomingCall  { appId, callId, from, to, isWhatsApp }
 *     UAFIncomingCallEnded      { appId, callId }   ringing stopped (any reason)
 *     UAFCallHostState          { appId, state: 'disabled' | 'idle' | 'in-call' }
 *     UAFCallHostSize           { appId, width, height }   CallBar size → frame size
 *     UAFCallHostDrag           { appId, phase, dx, dy }   CallBar dragged → move frame
 *
 *   extension → iframe  (ulgId 'UA_FLOAT_APP_CALL')
 *     acceptIncomingCall | declineIncomingCall | ignoreIncomingCall  { callId }
 *
 * 'in-call' asks the extension to show the frame (CallBar = mute, keypad,
 * hang up); 'idle' hides it again once CallBar has dismissed the call.
 */

import { useEffect, useRef, type RefObject } from 'react';
import { useCallStore } from '@/stores/call-store';
import { useAppStore } from '@/stores/app-store';
import { useSettingsStore } from '@/stores/settings-store';
import { isCallBarCall, isLiveCallStatus } from '@/types/voice';

export const IS_APPCURSOR_CALL_HOST = (() => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('target') === 'appcursor' && params.get('callHost') === '1' && window.parent !== window;
  } catch {
    return false;
  }
})();

// The extension shows this frame over the CRM page while a call is live.
// Clear the page background so only the CallBar is visible, not a white box.
if (IS_APPCURSOR_CALL_HOST) {
  document.documentElement.style.background = 'transparent';
  document.body.style.background = 'transparent';
}

// How long voice calling must stay off before the frame asks to be removed.
const DISABLED_GRACE_MS = 60_000;

function postToExtension(data: Record<string, unknown>) {
  // The parent is the CRM page the extension injected us into. Use its exact
  // origin when the browser exposes it, so caller numbers never go to '*'.
  const targetOrigin = window.location.ancestorOrigins?.[0] || '*';
  window.parent.postMessage({ ulgId: 'UA_FLOAT_APP_CONTENT', ...data }, targetOrigin);
}

export function useAppCursorCallBridge() {
  const activeCalls = useCallStore((s) => s.activeCalls);
  const dismissedCallIds = useCallStore((s) => s.dismissedCallIds);
  const extensionName = useAppStore((s) => s.extensionName);
  const settingsLoaded = useSettingsStore((s) => s.settingsLoaded);
  const enableVoiceCalling = useSettingsStore((s) => s.enableVoiceCalling);

  const ringingSentRef = useRef<Set<string>>(new Set());
  const wasLiveRef = useRef<Set<string>>(new Set());
  // Calls answered from the toast. Shown even if answering fails (e.g. the
  // microphone is blocked) so CallBar shows the error instead of nothing.
  const acceptedRef = useRef<Set<string>>(new Set());
  const lastStateRef = useRef<string | null>(null);

  // Call apps (twiliocall) always run voice on their own page: the call manager
  // and call center view switch it on when they mount (crm-call-manager.tsx,
  // call-center-view.tsx). This frame mounts neither, so it read the org flag —
  // often never saved — as off and removed itself. Do the same here.
  const isCallApp = extensionName?.split('for')[0] === 'twiliocall';
  useEffect(() => {
    if (IS_APPCURSOR_CALL_HOST && isCallApp && settingsLoaded && !enableVoiceCalling) {
      useSettingsStore.setState({ enableVoiceCalling: true });
    }
  }, [isCallApp, settingsLoaded, enableVoiceCalling]);

  // Voice calling off for this app → nothing to host; the extension removes the frame.
  // settingsLoaded turns true before the real Firestore values arrive (cached
  // prefs, or the safety net while the org id is still resolving — both with
  // voice calling at its default false). Only report 'disabled' once voice has
  // stayed off for a while, or a transient false removes a working frame.
  useEffect(() => {
    if (!IS_APPCURSOR_CALL_HOST || isCallApp || !settingsLoaded || !extensionName || enableVoiceCalling) return;
    const timer = setTimeout(() => {
      postToExtension({ action: 'UAFCallHostState', appId: extensionName, state: 'disabled' });
      lastStateRef.current = 'disabled';
    }, DISABLED_GRACE_MS);
    return () => clearTimeout(timer);
  }, [isCallApp, settingsLoaded, enableVoiceCalling, extensionName]);

  // Forward ringing calls + whether the call bar should be visible.
  useEffect(() => {
    if (!IS_APPCURSOR_CALL_HOST || !extensionName) return;

    const ringing = activeCalls.filter((c) => c.direction === 'inbound' && c.status === 'ringing');
    const ringingIds = new Set(ringing.map((c) => c.id));

    for (const call of ringing) {
      if (ringingSentRef.current.has(call.id)) continue;
      ringingSentRef.current.add(call.id);
      postToExtension({
        action: 'renderUAFAppIncomingCall',
        appId: extensionName,
        callId: call.id,
        from: (call.customerNumber || '').replace('whatsapp:', ''),
        to: (call.companyCallerId || '').replace('whatsapp:', ''),
        isWhatsApp: call.callChannel === 'whatsapp',
      });
    }
    for (const id of [...ringingSentRef.current]) {
      if (ringingIds.has(id)) continue;
      ringingSentRef.current.delete(id);
      postToExtension({ action: 'UAFIncomingCallEnded', appId: extensionName, callId: id });
    }

    // Same calls CallBar shows (isCallBarCall), except declined / ignored /
    // missed ringing calls — those never went live, so the frame should not pop
    // up for them. Using CallBar's own rule matters: an answered call canceled
    // while connecting is hidden by CallBar, and a looser check here left an
    // empty frame over the CRM page.
    for (const c of activeCalls) {
      if (isLiveCallStatus(c.status) && c.status !== 'ringing') wasLiveRef.current.add(c.id);
    }
    const inCall = activeCalls.some(
      (c) =>
        (wasLiveRef.current.has(c.id) || acceptedRef.current.has(c.id)) &&
        isCallBarCall(c, dismissedCallIds)
    );
    const state = inCall ? 'in-call' : 'idle';
    if (lastStateRef.current !== state && lastStateRef.current !== 'disabled') {
      lastStateRef.current = state;
      postToExtension({ action: 'UAFCallHostState', appId: extensionName, state });
    }
  }, [activeCalls, dismissedCallIds, extensionName]);

  // Toast buttons in the extension → call store actions.
  useEffect(() => {
    if (!IS_APPCURSOR_CALL_HOST) return;
    const handler = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      const data = event.data as { ulgId?: string; action?: string; callId?: string } | null;
      if (!data || data.ulgId !== 'UA_FLOAT_APP_CALL' || !data.callId) return;
      // Only a call this frame announced and that is still ringing — the
      // parent page can't use these to answer or end any other call.
      if (!ringingSentRef.current.has(data.callId)) return;
      const store = useCallStore.getState();
      switch (data.action) {
        case 'acceptIncomingCall':
          acceptedRef.current.add(data.callId);
          store.acceptCall(data.callId);
          break;
        case 'declineIncomingCall':
          store.endCall(data.callId);
          break;
        case 'ignoreIncomingCall':
          store.ignoreCall(data.callId);
          break;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
}

/**
 * Drag of the embedded CallBar. The frame can't move itself, so the extension
 * moves it: dx/dy = screen px the pointer moved since the drag started.
 */
export function postCallHostDrag(phase: 'start' | 'move' | 'end', dx = 0, dy = 0) {
  const appId = useAppStore.getState().extensionName;
  if (!IS_APPCURSOR_CALL_HOST || !appId) return;
  postToExtension({ action: 'UAFCallHostDrag', appId, phase, dx, dy });
}

/** Report the CallBar's size so the extension can fit the frame to it. */
export function useCallHostSizeReporter(ref: RefObject<HTMLElement | null>) {
  const extensionName = useAppStore((s) => s.extensionName);

  useEffect(() => {
    const el = ref.current;
    if (!IS_APPCURSOR_CALL_HOST || !el || !extensionName) return;
    let last = '';
    const observer = new ResizeObserver(() => {
      const width = Math.ceil(el.offsetWidth);
      const height = Math.ceil(el.offsetHeight);
      if (!width || !height || `${width}x${height}` === last) return;
      last = `${width}x${height}`;
      postToExtension({ action: 'UAFCallHostSize', appId: extensionName, width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, extensionName]);
}
